import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import deliveryFleetService from '../../services/deliveryFleetService';
import { usePermissions } from '../../context/PermissionContext';

function AddDriverModal({ show, onClose, onSaved }) {
  const blank = { firstName: '', lastName: '', phone: '', email: '', vehicleMake: '', vehicleModel: '', vehiclePlate: '', zelleHandle: '' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState(null);

  useEffect(() => { if (show) { setForm(blank); setError(''); setCredentials(null); } }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) { setError('First and last name are required.'); return; }
    setSaving(true); setError('');
    try {
      const { driver, credentials: creds } = await deliveryFleetService.createDriver(form);
      setCredentials(creds);
      onSaved(driver);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to add driver.');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Driver</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              {credentials ? (
                <div className="alert alert-success">
                  <p className="mb-2">Driver created! Share these login details with them (shown once):</p>
                  <p className="mb-1"><strong>Badge:</strong> {credentials.badgeNumber}</p>
                  <p className="mb-0"><strong>Temp password:</strong> {credentials.tempPassword}</p>
                </div>
              ) : (
                <>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <label className="form-label small mb-1">First Name *</label>
                      <input className="form-control form-control-sm" value={form.firstName} onChange={e => set('firstName', e.target.value)} autoFocus />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-1">Last Name *</label>
                      <input className="form-control form-control-sm" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
                    </div>
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <label className="form-label small mb-1">Phone</label>
                      <input className="form-control form-control-sm" value={form.phone} onChange={e => set('phone', e.target.value)} />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-1">Email</label>
                      <input className="form-control form-control-sm" value={form.email} onChange={e => set('email', e.target.value)} />
                    </div>
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-5">
                      <label className="form-label small mb-1">Vehicle Make</label>
                      <input className="form-control form-control-sm" value={form.vehicleMake} onChange={e => set('vehicleMake', e.target.value)} />
                    </div>
                    <div className="col-5">
                      <label className="form-label small mb-1">Vehicle Model</label>
                      <input className="form-control form-control-sm" value={form.vehicleModel} onChange={e => set('vehicleModel', e.target.value)} />
                    </div>
                    <div className="col-2">
                      <label className="form-label small mb-1">Plate</label>
                      <input className="form-control form-control-sm" value={form.vehiclePlate} onChange={e => set('vehiclePlate', e.target.value)} />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Zelle Handle <small className="text-muted">(email or phone used for payout)</small></label>
                    <input className="form-control form-control-sm" value={form.zelleHandle} onChange={e => set('zelleHandle', e.target.value)} />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>{credentials ? 'Close' : 'Cancel'}</button>
              {!credentials && (
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Add Driver'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function DriversPage() {
  const navigate = useNavigate();
  const { hasModule } = usePermissions();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = () => deliveryFleetService.listDrivers().then(setDrivers).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="container mt-4" style={{ maxWidth: 960 }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">Drivers</h4>
        <div className="d-flex gap-2">
          {hasModule('deliveryfleet.dispatch') && (
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/delivery-fleet')}>🗺 Dispatch Board</button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ Add Driver</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><span className="spinner-border" /></div>
      ) : drivers.length === 0 ? (
        <div className="text-center text-muted py-5">
          <p className="mb-2">No drivers yet.</p>
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowModal(true)}>Add your first driver</button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Driver</th><th>Vehicle</th><th>Agreement</th><th className="text-end">Deliveries</th>
                <th className="text-end">Earned</th><th className="text-end">Owed</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map(d => (
                <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/delivery-fleet/drivers/${d.id}`)}>
                  <td>
                    <div className="fw-semibold">{d.firstName} {d.lastName}</div>
                    <div className="text-muted small">{d.phone}</div>
                  </td>
                  <td className="small">{[d.vehicleMake, d.vehicleModel].filter(Boolean).join(' ') || '—'}</td>
                  <td>{d.agreementSignedAt ? <span className="badge bg-success">Signed</span> : <span className="badge bg-warning text-dark">Not signed</span>}</td>
                  <td className="text-end">{d.earnings.deliveriesCompleted}</td>
                  <td className="text-end">${d.earnings.totalEarnedUsd.toFixed(2)}</td>
                  <td className="text-end">{d.earnings.totalOwedUsd > 0 ? <span className="text-danger fw-semibold">${d.earnings.totalOwedUsd.toFixed(2)}</span> : '$0.00'}</td>
                  <td>
                    <span className={`badge ${d.isAvailableForDelivery ? 'bg-success' : 'bg-secondary'}`}>
                      {d.isAvailableForDelivery ? 'Available' : 'Offline'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddDriverModal show={showModal} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />
    </div>
  );
}
